import Tree from '../Tree';
import { FullscreenExitOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import { useBreadcrumbRoutes, Wrapper, FormTitle, Button, Tag, useTheme } from '@maxtropy/components';
import { Layout } from 'antd';
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import styles from './index.module.scss';
import { CircuitType } from '@/api/circuit';
import { enterFullScreen, exitFullScreen, initFullScreen } from './full';
import DetailDrawer from '@/pages/CircuitManage/components/CircuitTopologicalGraghTree/DetailDrawer';
import DragResize from '@/components/DragResize';
import {
  apiV2CircuitCircuitDataByRoomIdPost,
  apiV2CircuitCircuitTreeByRoomIdPost,
  apiV2CircuitCircuitTreeWithAllRoomPost,
  V2CircuitCircuitTreeWithAllRoomPostResponse,
} from '@maxtropy/dmes-apis-v2';
import { isNil } from 'lodash-es';
import { GraphProps, levelDisplay } from '../../utils';
import { EdgeConfig, NodeConfig } from '@antv/g6';
import StationGraph from '../TopologicalGraph/StationGraph';
import ViewGraph from '../TopologicalGraph/ViewGraph';
import { keepTwoUndefined } from '@/utils/utils';
import { useHasPermission } from '@/utils/hooks';
import { PermissionsType } from '@/common/permissionsConst';

const { Content, Sider } = Layout;

function transformCircuitTreeWithCombos(data: any[]): {
  nodes: NodeConfig[];
  edges: EdgeConfig[];
  combos: { id: string; label: string }[];
} {
  const nodesMap = new Map<string, NodeConfig>();
  const edges: EdgeConfig[] = [];
  const combos: { id: string; label: string; level: number }[] = [];

  for (const room of data) {
    const comboId = `room-${room.roomId}`;
    combos.push({
      id: comboId,
      level: room.level,
      label: room.roomName || '无所属站房',
    });

    const traverse = (node: any, parentId: string | null = null) => {
      const id = String(node.circuitId);
      const name = node.circuitName;

      // 只添加一次节点
      if (!nodesMap.has(id)) {
        nodesMap.set(id, {
          id,
          value: {
            name,
            type: node.type,
            hasOu: node.hasOu,
          },
          comboId,
        });
      }

      // 处理 source 连线
      if (node.source?.circuitId) {
        edges.push({
          source: String(node.source.circuitId),
          target: id,
        });
      }

      // 处理母联（双向连接）
      if (node.rightTarget?.circuitId) {
        edges.push({
          source: String(node.rightTarget.circuitId),
          target: id,
        });
      }

      // 处理子节点
      if (Array.isArray(node.children)) {
        for (const child of node.children) {
          edges.push({
            source: id,
            target: String(child.circuitId),
          });
          traverse(child, id);
        }
      }
    };

    for (const circuit of room.circuitList || []) {
      traverse(circuit);
    }
  }

  return {
    nodes: Array.from(nodesMap.values()),
    edges,
    combos,
  };
}

function deduplicateBusCouplers(tree: any[]): {
  cleanTree: any[];
  busCouplers: any[]; // 收集被“去重”的母联节点，用于后续绘制 marker 或 edge
} {
  const seenBusIds = new Set<string>();
  const removedBusCouplers: any[] = [];

  const dfs = (nodes: any[]): any[] =>
    nodes
      .map(node => {
        let newNode = { ...node };

        if (node.children && node.children.length > 0) {
          newNode.children = dfs(node.children);
        }

        if (node.value.type === CircuitType.BUS_COUPLER_CIRCUIT) {
          const id = String(node.id);
          if (seenBusIds.has(id)) {
            removedBusCouplers.push(node);
            return null;
          } else {
            seenBusIds.add(id);
          }
        }

        return newNode;
      })
      .filter(Boolean); // 去掉 null 节点

  return {
    cleanTree: dfs(tree),
    busCouplers: removedBusCouplers,
  };
}

const CircuitTopologicalGraghTree: FC = () => {
  const theme = useTheme();
  const [openSider, setOpenSider] = useState<boolean>(true);
  const [siderWidth, setSiderWidth] = useState(260);
  const [dragStatus, setDragStatus] = useState(false);

  const [roomId, setRoomId] = useState<number>();
  const [roomName, setRoomName] = useState<string>();

  const [selectedId, setSelectedId] = useState<number>();

  const [circuitDetailVisible, setCircuitDetailVisible] = useState<boolean>(false);
  const [currentCircuitInfo, setCurrentCircuitInfo] = useState<any>();

  const routesContext = useBreadcrumbRoutes();

  const [viewInfo, setViewInfo] = useState<V2CircuitCircuitTreeWithAllRoomPostResponse>();

  const [busCouplerList, setBusCouplerList] = useState<GraphProps[]>([]);
  const [level, setLevel] = useState<number>(0);

  const [treeData, setTreeData] = useState<GraphProps>();
  const [loading, setLoading] = useState(false);
  const hasViewPermission = useHasPermission(PermissionsType.B_OVERALLTOPOLOGYMAP);

  // canvas样式
  const ref = useRef<any>(null);

  // 全屏
  const [isFullScreen, setIsFullScreen] = useState<boolean>(false);

  const viewData = useMemo(() => {
    if (!isNil(roomId)) return;
    const { nodes, edges, combos } = transformCircuitTreeWithCombos(viewInfo?.powerRoomList ?? []);
    return { nodes, edges, combos };
  }, [roomId, viewInfo]);

  useEffect(() => {
    if (!roomId) return;
    setCircuitDetailVisible(false);

    const fetchData = async () => {
      setLoading(true);
      try {
        // 并行发起请求
        const [treeRes, dataRes] = await Promise.all([
          apiV2CircuitCircuitTreeByRoomIdPost({ id: Number(roomId) }),
          apiV2CircuitCircuitDataByRoomIdPost({ id: Number(roomId) }),
        ]);

        const structList = treeRes?.circuitList ?? [];
        const dataList = dataRes?.list ?? [];

        // 构建节点结构
        const cusTree = (tree?: any[]): GraphProps[] => {
          if (!tree) return [];

          return tree.map(item => {
            const data = dataList.find(k => k.circuitId === item.circuitId);
            const targetInCurrentStation = dataList.find(k => k.circuitId === item.target?.circuitId);
            return {
              id: String(item.circuitId),
              value: {
                id: item.circuitId,
                circuitId: item.circuitId,
                name: item.circuitName,
                type: item.type,
                electricCurrentA: keepTwoUndefined(data?.electricCurrentA),
                electricCurrentB: keepTwoUndefined(data?.electricCurrentB),
                electricCurrentC: keepTwoUndefined(data?.electricCurrentC),
                target: item.target,
                targetInCurrentStation: !!targetInCurrentStation,
                power: keepTwoUndefined(data?.power),
                hasOu: item?.hasOu,
                hasDevice: item?.hasDevice,
                switchStatus: item.switchStatus,
                isAlarm: data?.isAlarm,
              },
              children: item.children ? cusTree(item.children) : [],
            };
          });
        };

        const { cleanTree, busCouplers } = deduplicateBusCouplers(cusTree(structList));
        setBusCouplerList(busCouplers);

        setTreeData({
          id: 'virtual-root',
          children: cleanTree,
          value: {
            id: 'virtual-root',
            name: '--',
          },
        });
      } catch (error) {
        console.error('加载数据失败', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [roomId]);

  useEffect(() => {
    if (!hasViewPermission) return;
    if (isNil(roomId)) {
      apiV2CircuitCircuitTreeWithAllRoomPost({}).then(res => {
        setViewInfo(res);
      });
    }
  }, [roomId, hasViewPermission]);

  //全屏
  const handleFullScreen = () => {
    if (isFullScreen) {
      exitFullScreen();
    } else {
      enterFullScreen(ref.current);
    }
    setIsFullScreen(!isFullScreen);
  };

  const screenChange = (isFull: boolean) => {
    setIsFullScreen(isFull);
  };

  const dragChange = (width: number) => {
    if (openSider) {
      setSiderWidth(width);
    }
  };

  useEffect(() => {
    //初始化
    initFullScreen(screenChange);
  }, []);

  return (
    <div style={{ position: 'relative' }}>
      <Wrapper routes={[...(routesContext?.routes ?? [])]} hasPadding={false}>
        <Layout className={styles.layout}>
          <Sider
            theme="light"
            width={openSider ? siderWidth : 0}
            style={{ transition: dragStatus ? 'none' : 'all 0.2s', background: 'var(--mx-module-bg-color)' }}
          >
            <DragResize init={260} dragChange={dragChange} dragStatus={setDragStatus} />
            <Tree setRoomId={setRoomId} setRoomName={setRoomName} setLevel={setLevel} />
          </Sider>

          <Content className={styles.content}>
            <div className="toggleBtn" style={{ left: openSider ? -30 : 0 }} onClick={() => setOpenSider(!openSider)}>
              {openSider ? <LeftOutlined /> : <RightOutlined />}
            </div>
            <div className={styles.container}>
              <FormTitle
                style={{ marginBottom: 16 }}
                title={
                  <div className={styles.headerTitle}>
                    <div>{roomName ?? '整体拓扑图'}</div>
                    <div>
                      <Tag border="solid" color="default" style={{ marginLeft: 8 }}>
                        {levelDisplay[level]}
                      </Tag>
                    </div>
                  </div>
                }
                extraContent={
                  <Button type="primary" icon={<FullscreenExitOutlined />} onClick={handleFullScreen}>
                    全屏
                  </Button>
                }
              />
              <div
                ref={ref}
                style={{
                  flex: 1,
                  backgroundColor: 'var(--mx-module-bg-color)',
                }}
              >
                {isNil(roomId) ? (
                  <ViewGraph
                    data={viewData}
                    theme={theme}
                    fullScreen={isFullScreen}
                    focusId={selectedId}
                    setCurrentCircuitInfo={setCurrentCircuitInfo}
                    setCircuitDetailVisible={setCircuitDetailVisible}
                  />
                ) : (
                  <StationGraph
                    data={treeData}
                    theme={theme}
                    busCouplers={busCouplerList}
                    focusId={selectedId}
                    fullScreen={isFullScreen}
                    setCurrentCircuitInfo={setCurrentCircuitInfo}
                    setCircuitDetailVisible={setCircuitDetailVisible}
                  ></StationGraph>
                )}
              </div>
            </div>
          </Content>
        </Layout>
      </Wrapper>
      <DetailDrawer
        open={circuitDetailVisible}
        setOpen={setCircuitDetailVisible}
        currentCircuitInfo={currentCircuitInfo}
      />
    </div>
  );
};

export default CircuitTopologicalGraghTree;
